import { useCallback, useMemo, useState } from 'react'

/**
 * PrimeReact sıralama yönü: `1` artan, `-1` azalan; `0`/`null`/`undefined` → **sıralama yok**
 * (`removableSort` ile üçüncü tıklamada bu duruma dönülür → sunucu varsayılan düzeni geçerli olur).
 */
export type SortOrder = 1 | -1 | 0 | null | undefined

/** PrimeReact `onSort` olayının kullandığımız alanları (`DataTableStateEvent`'in alt kümesi). */
export interface TableSortEvent {
  sortField: string
  sortOrder: SortOrder
}

export interface TableSort {
  /** Doğrudan `<DataTable sortField={...}>`'a verilir. */
  sortField: string | undefined
  /** Doğrudan `<DataTable sortOrder={...}>`'a verilir. */
  sortOrder: SortOrder
  /** Doğrudan `<DataTable onSort={...}>`'a verilir (`lazy` tablolarda zorunlu). */
  onSort: (e: TableSortEvent) => void
  /**
   * Spring Data `sort` istek parametresi: `alan,asc|desc`. Sıralama yoksa **`undefined`** —
   * `axios` `undefined` parametreleri hiç göndermez → sunucu kendi varsayılan düzenini uygular.
   * Sorgu anahtarına (React Query `queryKey`) da bu değer konur.
   */
  sort: string | undefined
}

/**
 * **Sunucu-taraflı (lazy) tablolarda ortak kolon sıralaması.** Tüm listelerde tekrar eden
 * `sortField`/`sortOrder` durumu + `alan,asc|desc` dizgisi kurulumunu tek yerde toplar; ekran yalnız
 * dönen üç prop'u `DataTable`'a, `sort` değerini de sorgusuna geçirir:
 *
 * ```tsx
 * const sorting = useTableSort(() => setFirst(0))
 * const list = useThings({ page, size, sort: sorting.sort })
 * <DataTable lazy sortField={sorting.sortField} sortOrder={sorting.sortOrder} onSort={sorting.onSort}>
 *   <Column field="name" header={...} sortable />
 * ```
 *
 * **`onChange` ile sayfa sıfırlanır:** sıralama değişince kullanıcının bulunduğu sayfa numarası
 * anlamını yitirir (3. sayfadaki kayıt yeni düzende 1. sayfaya düşebilir) → çağıran `setFirst(0)`
 * verir. Sayfalama durumu bilinçli olarak bu hook'un DIŞINDADIR: her ekranın kendi `first`/`rows`
 * durumu (ve sekme başına ayrı kopyası) vardır.
 *
 * **Yön `0`/`null` iken `sort` gönderilMEZ:** `removableSort` ile sıralamayı kaldıran kullanıcı,
 * "alfabetik artan"a değil **sunucu varsayılanına** döner (ör. şablon parametrelerinde grup → kimlik).
 * Sıralamayı programatik olarak kaldıran ayrı bir işlev **yoktur**: kullanıcı bunu başlığın üçüncü
 * tıklamasıyla zaten yapar ve "Temizle" düğmesi bilinçli olarak yalnız **filtreleri** sıfırlar.
 *
 * Alan adı sunucuya **olduğu gibi** gider; hangi adların kabul edildiği sunucudaki beyaz listede
 * tanımlıdır (`SortSupport`) — listede olmayan ad sessizce düşürülür, `500` üretmez.
 *
 * **`initial` — ekranın açılıştaki düzeni** (ör. Yönetici Raporu'nun alt tablosu: termin ↑). Verilmezse
 * sıralama boş başlar ve sunucu kendi varsayılanını uygular. Verildiğinde başlıktaki sıralama oku da
 * açılışta görünür → kullanıcı listenin neye göre sıralı olduğunu tahmin etmek zorunda kalmaz.
 */
export function useTableSort(onChange?: () => void, initial?: TableSortEvent): TableSort {
  const [sortField, setSortField] = useState<string | undefined>(initial?.sortField)
  const [sortOrder, setSortOrder] = useState<SortOrder>(initial?.sortOrder ?? null)

  const onSort = useCallback(
    (e: TableSortEvent) => {
      setSortField(e.sortField)
      setSortOrder(e.sortOrder)
      onChange?.()
    },
    [onChange],
  )

  const sort = useMemo(
    () =>
      sortField && (sortOrder === 1 || sortOrder === -1)
        ? `${sortField},${sortOrder === 1 ? 'asc' : 'desc'}`
        : undefined,
    [sortField, sortOrder],
  )

  return { sortField, sortOrder, onSort, sort }
}
